import {createAsyncThunk} from '@reduxjs/toolkit'

import {includes} from '@shared/helpers/collection'
import {qs} from '@shared/helpers/queryString'

import {PopupSize} from '@web/core/constants'
import {ROUTER_ORDER_BOOK_PARCEL_PICKUP, ROUTER_ORDER_SALE_HISTORY} from '@web/core/routes'
import {changeBrowserLocation, closeExistingPopup, openPopup} from '@web/helpers/browser'
import {isMobile} from '@web/helpers/userAgent'
import {pageSetInfoSelector} from '@web/slices/common/slice'
import {AssignmentType, AssignmentTypeType} from '@web/slices/productBundle/slice'

import {LocationChangeHistoryWithRouteParam, NavigationTypeSelectableParam, ThunkApi} from '../interfaces'

import navigateToRoute from './navigateToRoute'

const getPushActionPayloadMap = ({
    orderNo,
    productOrderNo,
}: {
    orderNo: string
    productOrderNo: string
}): Record<AssignmentTypeType, string> => ({
    CHECK_ORDER: '',
    DISPATCH: ROUTER_ORDER_SALE_HISTORY.DELIVERY_DISPATCH.getPath(`${orderNo}/product/${productOrderNo}`),
    SALE_REJECT: ROUTER_ORDER_SALE_HISTORY.CANCEL_SALE.getPath(`${orderNo}/product/${productOrderNo}`),
    CANCEL_ADMISSION: '',
    CANCEL_REJECT: ROUTER_ORDER_SALE_HISTORY.CANCEL_REJECT.getPath(`${orderNo}/product/${productOrderNo}`),
    TRACK_DELIVERY: '',
    BOOKING_VISIT_DELIVERY_DISPATCH: ROUTER_ORDER_BOOK_PARCEL_PICKUP.ROOT.getPath(`${productOrderNo}/dispatch`),
    BOOKING_VISIT_DELIVERY_CANCEL_REJECT: ROUTER_ORDER_BOOK_PARCEL_PICKUP.ROOT.getPath(
        `${productOrderNo}/cancelReject`,
    ),
    RETURN_ADMISSION: ROUTER_ORDER_SALE_HISTORY.RETURN_ADMISSION.getPath(`${orderNo}/product/${productOrderNo}`),
    RETURN_REJECT: ROUTER_ORDER_SALE_HISTORY.RETURN_REJECT.getPath(`${orderNo}/product/${productOrderNo}`),
})

type NavigateToSaleHistoryParam = NavigationTypeSelectableParam &
    LocationChangeHistoryWithRouteParam & {
        type: AssignmentTypeType
        orderNo: string
        productOrderNo: string
    }

/** 판매내역 */
const navigateToSaleHistoryAssignments = createAsyncThunk<
    string | unknown,
    NavigateToSaleHistoryParam,
    ThunkApi<{
        code: string
        message: string
    }>
>(
    'navigateTo/sale/history/assignments',
    async (
        {historyType = 'PUSH', forceLocationChange, type, orderNo, productOrderNo, routeBased = false},
        {dispatch, getState, rejectWithValue},
    ) => {
        const state = getState()
        const {isSaleHistoryDetail} = pageSetInfoSelector(state)
        const action = forceLocationChange || isMobile ? 'ROUTE' : 'POPUP'
        const path = getPushActionPayloadMap({orderNo, productOrderNo})[type]
        const isBookingDelivery = includes(type, [
            AssignmentType.BOOKING_VISIT_DELIVERY_DISPATCH,
            AssignmentType.BOOKING_VISIT_DELIVERY_CANCEL_REJECT,
        ])
        const popupSize = isBookingDelivery ? PopupSize.bookingVisitDelivery : PopupSize.common

        if (!path) {
            return rejectWithValue({
                code: 'NOT_FOUND',
                message: 'Not FOUND',
            })
        }
        if (action === 'ROUTE') {
            const url = qs.stringifyUrl({
                url: path,
                query: {
                    action: 'ROUTE',
                    rurl: window.location.href,
                    entry: isSaleHistoryDetail ? 'DETAIL' : 'MAIN',
                },
            })
            if (routeBased) {
                dispatch(
                    navigateToRoute({
                        historyType,
                        path: url,
                    }),
                )
            } else {
                changeBrowserLocation({
                    type: historyType,
                    url,
                })
            }
            return undefined
        }
        const url = qs.stringifyUrl({
            url: path,
            query: {
                action: 'POPUP',
                entry: isSaleHistoryDetail ? 'DETAIL' : 'MAIN',
            },
        })
        await closeExistingPopup({key: 'SALE_HISTORY_POPUP', url})

        return openPopup({
            url,
            key: 'SALE_HISTORY_POPUP',
            size: popupSize,
            leaveType: 'close',
        })
    },
)

export default navigateToSaleHistoryAssignments
